/**
 * Best-effort carrier detection for shipment tracking numbers.
 *
 * Patterns are intentionally simple regex heuristics — no checksum
 * validation. False positives are possible (especially the digit-length
 * branches for FedEx/USPS/DHL), so callers should fall through to plain
 * text when `detectCarrier` returns null.
 */

export type Carrier = 'UPS' | 'USPS' | 'FedEx' | 'DHL';

export interface CarrierMatch {
  carrier: Carrier;
  url: string;
}

export function detectCarrier(rawTrackingNumber: string): CarrierMatch | null {
  const tn = rawTrackingNumber.replace(/\s+/g, '').toUpperCase();
  if (/^1Z[0-9A-Z]{16}$/.test(tn)) {
    return { carrier: 'UPS', url: `https://www.ups.com/track?tracknum=${encodeURIComponent(tn)}` };
  }
  if (/^9[0-9]{21}$/.test(tn) || /^(94|93|92|82|70)[0-9]{18,20}$/.test(tn)) {
    return {
      carrier: 'USPS',
      url: `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${encodeURIComponent(tn)}`,
    };
  }
  if (/^[0-9]{12}$/.test(tn) || /^[0-9]{15}$/.test(tn) || /^[0-9]{20}$/.test(tn)) {
    return { carrier: 'FedEx', url: `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(tn)}` };
  }
  if (/^[0-9]{10,11}$/.test(tn)) {
    return { carrier: 'DHL', url: `https://www.dhl.com/en/express/tracking.html?AWB=${encodeURIComponent(tn)}` };
  }
  return null;
}
